import styled from "@emotion/styled";
import { DSButton } from "@hundred5/design-system";
import { useLingui } from "@lingui/react/macro";
import { useRef } from "react";
import { useFlashMessages } from "../flash-messages";

interface UploadButtonProps {
  uploading?: boolean;
  buttonName?: string;
  variant?: "tertiary" | "secondary" | "primary";
  name?: string;
  onChange: (event: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FormEvent<HTMLInputElement>) => void;
  size?: "big" | "small" | "xsmall";
  fullWidth?: boolean;
  typeOfFiles?: string;
}

export function UploadButton({
  name,
  buttonName,
  variant = "secondary",
  uploading,
  onChange,
  onBlur,
  size = "small",
  fullWidth = false,
  typeOfFiles = ".doc, .docx, .xls, .xlsx, .odt, .txt, .pdf, .jpg, .jpeg, .png",
}: UploadButtonProps) {
  const { t } = useLingui();
  const { addMessage } = useFlashMessages();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <StyledDSButton
      type="button"
      data-testid="upload-button-file"
      variant={variant ? variant : "secondary"}
      size={size}
      onClick={() => inputRef.current?.click()}
      fullWidth={fullWidth}
    >
      {buttonName ? buttonName : uploading ? t`Uploading...` : t`Upload file`}

      <Input
        name={name}
        data-testid="upload-file"
        type="file"
        ref={inputRef}
        disabled={uploading}
        accept={typeOfFiles}
        onChange={(event) => {
          const target = event.target as HTMLInputElement;
          const files = target.files;
          if (!files || files.length === 0) return;

          const acceptedExtensions = typeOfFiles
            .split(",")
            .map((ext) => ext.trim().toLowerCase());

          const isAllValid = Array.from(files).every((file) => {
            const fileExtension = file.name
              .substring(file.name.lastIndexOf("."))
              .toLowerCase();
            return acceptedExtensions.includes(fileExtension);
          });

          if (!isAllValid) {
            target.value = "";
            addMessage({
              type: "unsupported_file_type",
              supportedTypes: typeOfFiles,
            });
            return;
          }

          const isAllValidSize = Array.from(files).every((file) => {
            return file.size / 1024 / 1024 <= 100;
          });

          if (!isAllValidSize) {
            target.value = "";
            addMessage({ type: "file_too_large_error" });
            return;
          }

          onChange(event);
        }}
        onBlur={onBlur}
      />
    </StyledDSButton>
  );
}

const StyledDSButton = styled(DSButton)<{ fullWidth: boolean }>`
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
`;

const Input = styled.input`
  opacity: 0;
  width: 0;
`;
